"use client";

import { useRef, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle, AlertCircle, Mail, Building2 } from "lucide-react";

const VIDEO_URL =
  "https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260306_115329_5e00c9c5-4d69-49b7-94c3-9c31c60bb644.mp4";

export default function ContactForm() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.play().catch(() => {
      const handler = () => { v.play().catch(() => {}); document.removeEventListener("click", handler); };
      document.addEventListener("click", handler, { once: true });
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setStatus("sent");
        setForm({ name: "", email: "", company: "", message: "" });
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section
      style={{
        minHeight: "100vh",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        padding: "140px 24px 100px",
      }}
    >
      {/* Full-screen background video (same as hero) */}
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: 0,
        }}
      >
        <source src={VIDEO_URL} type="video/mp4" />
      </video>

      {/* Dark overlay for readability */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(10, 12, 16, 0.75)",
          zIndex: 1,
        }}
      />

      <div style={{ maxWidth: 680, width: "100%", margin: "0 auto", position: "relative", zIndex: 2 }}>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          style={{ textAlign: "center", marginBottom: 48 }}
        >
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: "0.78rem",
              fontWeight: 500,
              textTransform: "uppercase",
              letterSpacing: "0.12em",
              color: "rgba(255,255,255,0.4)",
              marginBottom: 16,
            }}
          >
            Contact Us
          </p>
          <h1
            style={{
              fontFamily: "var(--font-heading)",
              fontSize: "clamp(2rem, 4vw, 3rem)",
              color: "#fff",
              marginBottom: 16,
              lineHeight: 1.2,
            }}
          >
            Let&apos;s start a{" "}
            <em style={{ fontFamily: "var(--font-heading)" }}>conversation</em>
          </h1>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: "1.05rem",
              color: "rgba(255,255,255,0.55)",
              fontWeight: 300,
              lineHeight: 1.7,
              maxWidth: 520,
              margin: "0 auto",
            }}
          >
            Have a question about our plans, need a custom solution for your
            research firm or call center? We&apos;ll get back to you within one business day.
          </p>
        </motion.div>

        {status === "sent" ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            style={{
              background: "rgba(255,255,255,0.04)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 20,
              padding: "64px 40px",
              textAlign: "center",
              boxShadow: "0 8px 32px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)",
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "rgba(34,197,94,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
              }}
            >
              <CheckCircle size={32} color="#22c55e" strokeWidth={1.5} />
            </div>
            <h2
              style={{
                fontFamily: "var(--font-heading)",
                fontSize: "1.6rem",
                color: "#fff",
                marginBottom: 10,
              }}
            >
              Message sent!
            </h2>
            <p
              style={{
                fontFamily: "var(--font-body)",
                color: "rgba(255,255,255,0.5)",
                fontSize: "0.95rem",
                fontWeight: 300,
                lineHeight: 1.6,
              }}
            >
              Thank you for reaching out. We&apos;ll respond to your message within one business day.
            </p>
          </motion.div>
        ) : (
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            style={{
              background: "rgba(255,255,255,0.03)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              border: "1px solid rgba(255,255,255,0.07)",
              borderRadius: 20,
              padding: "40px 36px",
              display: "flex",
              flexDirection: "column",
              gap: 24,
              boxShadow: "0 8px 32px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.04)",
            }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label style={labelStyle}>Name *</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  style={inputStyle}
                  placeholder="Your name"
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label style={labelStyle}>Email *</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  style={inputStyle}
                  placeholder="you@company.com"
                />
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={labelStyle}>Company</label>
              <input
                type="text"
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                style={inputStyle}
                placeholder="Optional"
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={labelStyle}>Message *</label>
              <textarea
                required
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                style={{
                  ...inputStyle,
                  resize: "vertical",
                  minHeight: 130,
                  lineHeight: 1.6,
                }}
                placeholder="Tell us about your project, ask about our plans, or request a demo..."
              />
            </div>

            {status === "error" && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  color: "#f87171",
                  fontSize: "0.88rem",
                  fontFamily: "var(--font-body)",
                  fontWeight: 300,
                  background: "rgba(248,113,113,0.08)",
                  padding: "10px 16px",
                  borderRadius: 10,
                  border: "1px solid rgba(248,113,113,0.15)",
                }}
              >
                <AlertCircle size={16} />
                Something went wrong. Please try again or email us directly.
              </div>
            )}

            <button
              type="submit"
              disabled={status === "sending"}
              className="btn-primary"
              style={{
                padding: "16px 32px",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                alignSelf: "flex-start",
                opacity: status === "sending" ? 0.6 : 1,
                cursor: status === "sending" ? "not-allowed" : "pointer",
                fontSize: "0.95rem",
                fontWeight: 500,
                borderRadius: 12,
              }}
            >
              {status === "sending" ? "Sending..." : "Send Message"}
              <Send size={16} />
            </button>
          </motion.form>
        )}

        {/* Contact info strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          style={{
            marginTop: 40,
            display: "flex",
            justifyContent: "center",
            gap: 32,
            flexWrap: "wrap",
          }}
        >
          <a
            href="mailto:support@ethnicpath.com"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "rgba(255,255,255,0.45)",
              textDecoration: "none",
              fontFamily: "var(--font-body)",
              fontSize: "0.88rem",
              fontWeight: 300,
              transition: "color 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.8)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.45)")}
          >
            <Mail size={15} strokeWidth={1.5} />
            support@ethnicpath.com
          </a>
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              color: "rgba(255,255,255,0.45)",
              fontFamily: "var(--font-body)",
              fontSize: "0.88rem",
              fontWeight: 300,
            }}
          >
            <Building2 size={15} strokeWidth={1.5} />
            Powered by EthnicPath Research
          </span>
        </motion.div>
      </div>
    </section>
  );
}

const labelStyle: React.CSSProperties = {
  fontFamily: "var(--font-body)",
  fontSize: "0.82rem",
  fontWeight: 500,
  color: "rgba(255,255,255,0.5)",
  letterSpacing: "0.02em",
};

const inputStyle: React.CSSProperties = {
  fontFamily: "var(--font-body)",
  fontSize: "0.92rem",
  padding: "13px 16px",
  borderRadius: 10,
  border: "1px solid rgba(255,255,255,0.08)",
  background: "rgba(255,255,255,0.04)",
  color: "#fff",
  outline: "none",
  transition: "border-color 0.2s, background 0.2s",
};
